// Las acciones de la ficha, bajo el recorrido: como en el diseño de Víctor, un
// botón principal oscuro a todo el ancho y los demás con borde.
//
// El principal es el SIGUIENTE paso de transporte: marcarlo si no hay marca,
// confirmar la fecha si está marcado, cambiarla si ya está confirmada. Debajo,
// generar documentación, avisar al cliente y quitar la marca.
//
// Qué botón está habilitado lo decide `botonesFicha` (puro, con test). Desde
// 2026-11-03 la marca y el aviso valen con la carga en cualquier estado vivo;
// una carga que ya salió no está en la lista y el backend contesta 404.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogoModal } from '@/components/common/DialogoModal'
import { useAvisoCliente, useEstadoManual } from '@/hooks/useCargasSalida'
import type { AvisoClienteResultado, CargaSalida } from '@/types'
import {
  botonesFicha, datetimeLocalAIso, errorApiDe, isoADatetimeLocal, type ErrorApi,
} from '@/utils/fichaCarga'
import { fmtFechaHora } from '../formatoSalida'

interface Props {
  c: CargaSalida
  dark: boolean
  /** Abre el generador de documentos; `null` si no está disponible para la empresa. */
  onGenerarDocumentos: (() => void) | null
}

export function FichaEstadoManual({ c, dark, onGenerarDocumentos }: Props) {
  const { t } = useTranslation()
  const botones = botonesFicha(c)
  const manual = useEstadoManual(c.carga)
  const aviso = useAvisoCliente(c.carga)

  const [pidiendoFecha, setPidiendoFecha] = useState(false)
  const [fecha, setFecha] = useState('')
  const [confirmandoAviso, setConfirmandoAviso] = useState(false)
  const [error, setError] = useState<ErrorApi | null>(null)
  const [avisoReciente, setAvisoReciente] = useState<AvisoClienteResultado | null>(null)

  const msgError = (e: ErrorApi) =>
    t(`cargasSalida.ficha.errores.${e.codigo}`, {
      defaultValue: t('cargasSalida.ficha.errores.error_inesperado'),
      ...(e.valores ?? {}),
    })

  const motivoApagado = t('cargasSalida.ficha.manual.cargada')

  function marcar(estado: 'transporte' | null) {
    setError(null)
    manual.mutate({ estado }, { onError: e => setError(errorApiDe(e)) })
  }

  function abrirFecha() {
    setError(null)
    // Con una fecha ya confirmada se parte de ella: cambiarla es retocar, no repetir.
    setFecha(isoADatetimeLocal(c.fechaTransporteConfirmada))
    setPidiendoFecha(true)
  }

  function confirmarFecha() {
    const iso = datetimeLocalAIso(fecha)
    if (!iso) return
    setError(null)
    manual.mutate(
      { estado: 'transporte_confirmado', fecha_transporte: iso },
      {
        onSuccess: () => setPidiendoFecha(false),
        onError: e => setError(errorApiDe(e)),
      },
    )
  }

  function avisar() {
    setError(null)
    aviso.mutate(undefined, {
      onSuccess: r => { setAvisoReciente(r.data); setConfirmandoAviso(false) },
      onError: e => { setError(errorApiDe(e)); setConfirmandoAviso(false) },
    })
  }

  const btn = (activo: boolean, primario = false) =>
    `w-full text-[13px] font-semibold px-3 py-2.5 rounded-lg transition-colors ${
      activo
        ? `cursor-pointer ${primario
            ? (dark ? 'bg-slate-100 text-slate-900 hover:bg-white' : 'bg-slate-800 text-white hover:bg-slate-700')
            : (dark ? 'border border-white/15 text-slate-100 hover:bg-white/8' : 'border border-slate-300 text-slate-800 hover:bg-slate-50')}`
        : `cursor-not-allowed opacity-40 ${primario
            ? (dark ? 'bg-slate-100 text-slate-900' : 'bg-slate-800 text-white')
            : (dark ? 'border border-white/10 text-slate-400' : 'border border-slate-200 text-slate-500')}`
    }`
  const enlace = (activo: boolean) =>
    `text-[11px] font-semibold ${activo
      ? 'text-sky-600 hover:text-sky-500 hover:underline cursor-pointer'
      : 'cursor-not-allowed opacity-40 text-slate-500'}`

  const avisadoFecha = c.avisoClienteFecha ?? avisoReciente?.fecha ?? null
  const ocupado = manual.isPending
  const sinMarca = c.estadoManual == null

  // El principal: el siguiente paso de transporte.
  const principal = sinMarca
    ? { texto: t('cargasSalida.ficha.manual.marcarTransporte'), activo: botones.marcarTransporte, accion: () => marcar('transporte') }
    : {
        texto: t(c.estado === 'transporte_confirmado'
          ? 'cargasSalida.ficha.manual.cambiarFecha'
          : 'cargasSalida.ficha.manual.confirmarFecha'),
        activo: botones.confirmarTransporte,
        accion: abrirFecha,
      }

  return (
    <section className="flex flex-col gap-2">
      <button
        type="button"
        disabled={!principal.activo || ocupado}
        title={principal.activo ? undefined : motivoApagado}
        onClick={principal.accion}
        className={btn(principal.activo && !ocupado, true)}
      >
        {principal.texto}
      </button>
      <button
        type="button"
        disabled={!onGenerarDocumentos}
        title={onGenerarDocumentos
          ? undefined
          : t('cargasSalida.acciones.documentosNoDisponible', { empresa: c.empresa.toUpperCase() })}
        onClick={onGenerarDocumentos ?? undefined}
        className={btn(!!onGenerarDocumentos)}
      >
        {t('cargasSalida.ficha.generarDocumentacion')}
      </button>
      <button
        type="button"
        disabled={!botones.avisarCliente || aviso.isPending}
        title={botones.avisarCliente ? undefined : motivoApagado}
        onClick={() => setConfirmandoAviso(true)}
        className={btn(botones.avisarCliente && !aviso.isPending)}
      >
        {t('cargasSalida.ficha.aviso.boton')}
      </button>

      <div className="flex items-center justify-between gap-3 flex-wrap pt-0.5">
        <span className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
          {avisadoFecha && (c.avisoClientePor
            ? t('cargasSalida.ficha.aviso.avisado', { fecha: fmtFechaHora(avisadoFecha), por: c.avisoClientePor })
            : t('cargasSalida.ficha.aviso.avisadoSinAutor', { fecha: fmtFechaHora(avisadoFecha) }))}
        </span>
        <span className="flex items-center gap-3">
          {/* Sin marca, se puede confirmar la fecha directamente. */}
          {sinMarca && (
            <button
              type="button"
              disabled={!botones.confirmarTransporte || ocupado}
              onClick={abrirFecha}
              className={enlace(botones.confirmarTransporte && !ocupado)}
            >
              {t('cargasSalida.ficha.manual.confirmarFecha')}
            </button>
          )}
          {c.estado === 'transporte_confirmado' && (
            <button
              type="button"
              disabled={!botones.marcarTransporte || ocupado}
              onClick={() => marcar('transporte')}
              className={enlace(botones.marcarTransporte && !ocupado)}
            >
              {t('cargasSalida.ficha.manual.volverATransporte')}
            </button>
          )}
          {botones.quitarManual && (
            <button
              type="button"
              disabled={ocupado}
              onClick={() => marcar(null)}
              className={enlace(!ocupado)}
            >
              {t('cargasSalida.ficha.manual.quitar')}
            </button>
          )}
        </span>
      </div>

      {avisoReciente?.mock && (
        <p
          role="status"
          className={`text-[11px] rounded-lg px-3 py-2 ${
            dark ? 'bg-amber-500/10 text-amber-200 ring-1 ring-amber-400/30' : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200'
          }`}
        >
          {t('cargasSalida.ficha.aviso.simulado')}
        </p>
      )}

      {error && !pidiendoFecha && !confirmandoAviso && (
        <p role="alert" className={`text-[11px] rounded-lg px-3 py-2 ${
          dark ? 'bg-rose-500/10 text-rose-200 ring-1 ring-rose-400/30' : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
        }`}>
          {msgError(error)}
        </p>
      )}

      {pidiendoFecha && (
        <DialogoModal
          dark={dark}
          titulo={t('cargasSalida.ficha.manual.fechaTitulo')}
          confirmarTexto={t('cargasSalida.ficha.manual.fechaConfirmar')}
          cancelarTexto={t('cargasSalida.ficha.cancelar')}
          onConfirmar={confirmarFecha}
          onCancelar={() => { setPidiendoFecha(false); setError(null) }}
          confirmarDeshabilitado={!datetimeLocalAIso(fecha)}
          ocupado={manual.isPending}
          error={error ? msgError(error) : null}
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold">{t('cargasSalida.ficha.manual.fechaEtiqueta')}</span>
            <input
              type="datetime-local"
              value={fecha}
              onChange={e => setFecha(e.target.value)}
              className={`text-[12px] rounded-lg border px-2.5 py-1.5 ${
                dark ? 'bg-slate-950 border-white/10 text-slate-100' : 'bg-white border-slate-300 text-slate-900'
              }`}
            />
          </label>
        </DialogoModal>
      )}

      {confirmandoAviso && (
        <DialogoModal
          dark={dark}
          titulo={t('cargasSalida.ficha.aviso.confirmarTitulo')}
          confirmarTexto={t('cargasSalida.ficha.aviso.confirmarBoton')}
          cancelarTexto={t('cargasSalida.ficha.cancelar')}
          onConfirmar={avisar}
          onCancelar={() => setConfirmandoAviso(false)}
          ocupado={aviso.isPending}
        >
          {t('cargasSalida.ficha.aviso.confirmarTexto')}
        </DialogoModal>
      )}
    </section>
  )
}
